/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import {
  URL_GIFT_IMAGE,
  useBranch,
  useRequestGiftHistory,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import { Button, ShareModal, spacing, Text, useModal } from "@packages/ui";
import { intlFormat } from "date-fns";

import { PaymentSection } from "../../app/[locale]/settings/payment/PaymentSection";

const containerStyles: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
};

const rowStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  paddingBlock: spacing.ms,
  paddingInline: spacing.m,
  width: spacing.full,
};

const textStyles: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
  justifyContent: "center",
};

const redeemedStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedLow,
});

export const GiftHistory = () => {
  const { query: history } = useRequestGiftHistory();
  const t = useTranslations();
  const shareModal = useModal(ShareModal);
  const { gift } = useBranch();

  const handleShare = async (promo: string) =>
    shareModal.show({
      imgSrc: URL_GIFT_IMAGE,
      title: t("gift_sub_share_title"),
      link: await gift({ promoCode: promo }),
      description: promo,
      color: "#7166D2",
    });

  if (history.error) return null;

  if (!history.data?.gifts?.length) return null;

  return (
    <PaymentSection title={t("gift_hallow_purchased_gifts")} description={""}>
      <div css={containerStyles}>
        {history.data.gifts.map((gift) => (
          <div
            key={gift.promo_code}
            css={[rowStyles, !!gift.redemption && redeemedStyles]}
          >
            <div css={textStyles}>
              <Text as={"span"} type={"title"} size={"m"}>
                {intlFormat(new Date(gift.purchase_date), {
                  month: "short",
                  year: "numeric",
                  day: "numeric",
                })}
              </Text>
              <Text as={"span"} type={"body"} size={"s"}>
                {gift.promo_code}
              </Text>
            </div>
            {gift.redemption ? (
              <Text type={"button"} size={"s"}>
                {t("gift_hallow_redeemed")}
              </Text>
            ) : (
              <Button
                variant={"neutral"}
                size={"s"}
                onClick={() => handleShare(gift.promo_code)}
              >
                {t("general_word_share")}
              </Button>
            )}
          </div>
        ))}
      </div>
    </PaymentSection>
  );
};
